import React, { useState } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { BlueBorderButton, BlueButton } from 'components/Button';
import Modal, { ModalHeader, ModalMessage } from 'components/Modal';
import Break from 'components/Break';
import Text from 'components/Text';
import SelectableSearchInput from 'components/SelectableSearchInput';
import {
  EMAIL_MAX_LENGTH,
  ERROR_MESSAGES,
  SPECIAL_CHARACTERS_ALLOWED,
  USER_FRIENDLY_AUTH_ROLES,
  USER_FRIENDLY_TO_DATABASE_AUTH_ROLES,
  USER_NAME_MAX_LENGTH
} from 'utils/appConstants';
import {
  isEmailValid,
  isAssignedRoleValid,
  isNoSpecialCharactersValid,
  isLengthValid
} from 'utils/validate';

const authRoles = Object.keys(USER_FRIENDLY_AUTH_ROLES).map(
  key => USER_FRIENDLY_AUTH_ROLES[key]
);

const InputRow = styled.div`
  display: flex;
  justify-content: space-between;
  height: 33px;
  margin: 1em 0;
`;

const InputLabel = styled(ModalMessage)`
  flex: 1 1 40%;
  font-size: 12px;
`;

const Input = styled.input`
  color: #4a4a4a;
  flex: 1 1 60%;
  font-family: Crossten;
  font-size: 16px;
  box-sizing: border-box;
  border-radius: 2px;
  border: 1px solid #d8d8d8;
  outline: none;
  padding: 1em 0.5em;
  border: ${({ isError }) =>
    isError ? '1px solid #EA6E6D' : '1px solid #d8d8d8'};
`;

const DropdownInput = styled(SelectableSearchInput)`
  flex: 1 1 60%;

  [class*='-Control'] {
    height: 100%;
  }

  [class*='-singleValue'] {
    color: #4a4a4a;
    font-size: 16px;
  }
`;

const ButtonRow = styled.div``;

const InputErrorText = styled(Text)`
  color: #ea6e6d;
  font-size: 14px;
  margin: 12px;
`;

const AddUserModal = ({ users, onSubmit, onClose }) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [inputErrors, setInputErrors] = useState({
    firstName: [],
    lastName: [],
    role: [],
    email: []
  });

  const handleInputChange = (event, validationObject) => {
    const target = event.target;
    const value = target.value;
    const name = target.name;

    const { SPECIAL_CHARACTERS_ERROR, MAX_LENGTH_ERROR } = ERROR_MESSAGES;

    let errorMessages = [];
    if (validationObject) {
      if (
        validationObject.pattern &&
        !isNoSpecialCharactersValid(value, validationObject.pattern)
      ) {
        errorMessages.push(SPECIAL_CHARACTERS_ERROR);
      }
      if (
        validationObject.maxLength &&
        !isLengthValid(value, validationObject.maxLength)
      ) {
        errorMessages.push(MAX_LENGTH_ERROR(validationObject.maxLength));
      }

      if (errorMessages.length) {
        setInputErrors(prevInputErrors => ({
          ...prevInputErrors,
          [name]: errorMessages
        }));
      } else {
        setInputErrors(prevInputErrors => ({
          ...prevInputErrors,
          [name]: []
        }));
      }
    }

    if (name === 'firstName') {
      setFirstName(value);
    } else if (name === 'lastName') {
      setLastName(value);
    } else if (name === 'email') {
      setEmail(value);
    }
  };

  const handleSubmit = event => {
    event.preventDefault();

    const databaseRole =
      USER_FRIENDLY_TO_DATABASE_AUTH_ROLES[role.toUpperCase()];

    if (validatePayload()) {
      onSubmit({ firstName, lastName, email, role: databaseRole });
    }
  };

  const validatePayload = () => {
    const {
      MAX_LENGTH_ERROR,
      EMAIL_MAX_LENGTH_ERROR,
      EMAIL_ADDRESS_IN_USE_ERROR,
      SPECIAL_CHARACTERS_ERROR,
      INVALID_EMAIL_ERROR,
      INVALID_PERMISSIONS_ASSIGNMENT
    } = ERROR_MESSAGES;
    const invalidEmails = users.map(user => user.email);

    const databaseRole =
      USER_FRIENDLY_TO_DATABASE_AUTH_ROLES[role.toUpperCase()];

    let isValid = true;
    let errorMessages = [];

    const verifyName = verifyFirstLast => {
      if (!isLengthValid(verifyFirstLast, USER_NAME_MAX_LENGTH)) {
        errorMessages.push(MAX_LENGTH_ERROR(USER_NAME_MAX_LENGTH));
      }
      if (!isNoSpecialCharactersValid(verifyFirstLast)) {
        errorMessages.push(SPECIAL_CHARACTERS_ERROR);
      }
      if (errorMessages.length) {
        isValid = false;
        setInputErrors(prevInputErrors => ({
          ...prevInputErrors,
          verifyFirstLast: errorMessages
        }));
        errorMessages = [];
      }
    };
    verifyName(firstName);
    verifyName(lastName);

    //email
    if (!isEmailValid(email)) {
      errorMessages.push(INVALID_EMAIL_ERROR);
    }
    if (invalidEmails.includes(email)) {
      errorMessages.push(EMAIL_ADDRESS_IN_USE_ERROR);
    }
    if (!isLengthValid(email, EMAIL_MAX_LENGTH)) {
      errorMessages.push(EMAIL_MAX_LENGTH_ERROR);
    }
    if (errorMessages.length) {
      isValid = false;
      setInputErrors(prevInputErrors => ({
        ...prevInputErrors,
        email: errorMessages
      }));
      errorMessages = [];
    }

    //role
    if (!databaseRole) {
      errorMessages.push();
    } else if (!isAssignedRoleValid(databaseRole)) {
      errorMessages.push(INVALID_PERMISSIONS_ASSIGNMENT);
    }
    if (errorMessages.length) {
      isValid = false;
      setInputErrors(prevInputErrors => ({
        ...prevInputErrors,
        role: errorMessages
      }));
      errorMessages = [];
    }

    if (isValid) {
      return true;
    }
  };

  return (
    <Modal
      as="form"
      style={{
        padding: '2em 3em',
        minWidth: '575px',
        minHeight: '250px',
        boxSizing: 'border-box'
      }}
      onSubmit={handleSubmit}
    >
      <ModalHeader>Send Account Invitation</ModalHeader>

      <InputRow>
        <InputLabel>First Name</InputLabel>
        <Input
          required
          type="text"
          name="firstName"
          onChange={e =>
            handleInputChange(e, {
              maxLength: USER_NAME_MAX_LENGTH,
              pattern: SPECIAL_CHARACTERS_ALLOWED
            })
          }
          isError={inputErrors.firstName.length}
          value={firstName}
          maxLength={USER_NAME_MAX_LENGTH}
          pattern={SPECIAL_CHARACTERS_ALLOWED}
        />
      </InputRow>
      {!!inputErrors.firstName.length &&
        inputErrors.firstName.map((error, i) => (
          <InputErrorText key={i}>{error}</InputErrorText>
        ))}

      <InputRow>
        <InputLabel>Last Name</InputLabel>
        <Input
          required
          type="text"
          name="lastName"
          onChange={e =>
            handleInputChange(e, {
              maxLength: USER_NAME_MAX_LENGTH,
              pattern: SPECIAL_CHARACTERS_ALLOWED
            })
          }
          isError={inputErrors.lastName.length}
          value={lastName}
          // maxLength={USER_NAME_MAX_LENGTH}
          pattern={SPECIAL_CHARACTERS_ALLOWED}
        />
      </InputRow>
      {!!inputErrors.lastName.length &&
        inputErrors.lastName.map((error, i) => (
          <InputErrorText key={i}>{error}</InputErrorText>
        ))}

      <InputRow>
        <InputLabel>Email Address</InputLabel>
        <Input
          required
          type="email"
          name="email"
          onChange={e => handleInputChange(e, { maxLength: EMAIL_MAX_LENGTH })}
          isError={inputErrors.email.length}
          maxLength={EMAIL_MAX_LENGTH}
          value={email}
        />
      </InputRow>
      {!!inputErrors.email.length &&
        inputErrors.email.map((error, i) => (
          <Text color="#EA6E6D" fontSize="14px" margin="12px" key={i}>
            {error}
          </Text>
        ))}

      <InputRow>
        <InputLabel>Permissions</InputLabel>
        <DropdownInput
          name="role"
          isMulti={false}
          options={authRoles}
          onChange={selected => {
            const role = selected && selected.value ? selected.value : '';
            setRole(role);
            setInputErrors(prevInputErrors => ({
              ...prevInputErrors,
              role: []
            }));
          }}
          isError={inputErrors.role.length}
        />
      </InputRow>
      {!!inputErrors.role.length &&
        inputErrors.role.map((error, i) => (
          <Text color="#EA6E6D" fontSize="14px" margin="12px" key={i}>
            {error}
          </Text>
        ))}

      <Break margin="2em 0" />

      <ButtonRow>
        <BlueBorderButton margin="0 16px 0 0" type="button" onClick={onClose}>
          Close
        </BlueBorderButton>
        <BlueButton type="submit">Send Invitation</BlueButton>
      </ButtonRow>
    </Modal>
  );
};

export default AddUserModal;

AddUserModal.propTypes = {
  users: PropTypes.array.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired
};
